import { convertFromMarkup } from '@app/utils/mention'
import { EditorState } from 'draft-js'
import { List, Record } from 'immutable'
import { Area, IArea } from './Area'
import { AssignmentSource, IAssignmentSource } from './AssignmentSource'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import { AssociatedProperty, IAssociatedProperty } from './AssociatedProperty'
import { Attachment, IAttachment } from './Attachment'
import { Comment, IComment } from './Comment'
import { Company, ICompany } from './Company'
import { AssignmentPriority } from './enums'
import { IRelatedAttachment, RelatedAttachment } from './RelatedAttachment'
import { IReminder, Reminder } from './Reminder'

export interface IAssignment {
  readonly assignedTo: List<IAssociatedContact>
  readonly attachments: List<IAttachment>
  readonly author?: IAssociatedContact
  readonly comments: List<IComment>
  readonly company?: ICompany
  readonly contacts: List<IAssociatedContact>
  readonly deleted: boolean
  readonly dollarOpportunity: number
  readonly dueDate?: number
  readonly hasStarred: List<string>
  readonly id: string
  readonly isComplete: boolean
  readonly label: string
  readonly lotSize: IArea
  readonly markup: string
  readonly markupState: EditorState
  readonly mentions: List<string>
  readonly priority: AssignmentPriority
  readonly properties: List<IAssociatedProperty>
  readonly relatedAttachments: List<IRelatedAttachment>
  readonly reminders: List<IReminder>
  readonly source?: IAssignmentSource
  readonly tags: List<string>
  readonly title: string
}

const AssignmentRecord = Record({
  assignedTo: List<AssociatedContact>(),
  attachments: List<Attachment>(),
  author: undefined,
  comments: List<Comment>(),
  company: undefined,
  contacts: List<AssociatedContact>(),
  deleted: false,
  dollarOpportunity: 0,
  dueDate: undefined,
  hasStarred: List<string>(),
  id: '',
  isComplete: false,
  label: 'Other',
  lotSize: new Area(),
  markup: '',
  markupState: EditorState.createEmpty(),
  mentions: List<string>(),
  priority: AssignmentPriority.Medium,
  properties: List<AssociatedProperty>(),
  relatedAttachments: List<RelatedAttachment>(),
  reminders: List<Reminder>(),
  source: undefined,
  tags: List<string>(),
  title: '',
})

export class Assignment extends AssignmentRecord implements IAssignment {
  public static fromJS(initialState?: any) {
    if (!initialState) {
      return new Assignment()
    }

    const {
      assignedTo,
      attachments,
      author,
      comments,
      company,
      contacts,
      hasStarred,
      mentions,
      properties,
      relatedAttachments,
      reminders,
      source,
      size,
      tags,
      ...rest
    } = initialState

    return new Assignment({
      assignedTo: assignedTo
        ? List(
            assignedTo.map((a: IAssociatedContact) =>
              AssociatedContact.fromJS(a),
            ),
          )
        : List<AssociatedContact>(),
      attachments: attachments
        ? List(attachments.map((a: IAttachment) => Attachment.fromJS(a)))
        : List<Attachment>(),
      author: author ? AssociatedContact.fromJS(author) : undefined,
      comments: comments
        ? List(comments.map((a: IComment) => Comment.fromJS(a)))
        : List<Comment>(),
      company: company ? Company.fromJS(company) : undefined,
      contacts: contacts
        ? List(
            contacts.map((a: IAssociatedContact) =>
              AssociatedContact.fromJS(a),
            ),
          )
        : List<AssociatedContact>(),
      hasStarred: List(hasStarred || []),
      lotSize: Area.fromJS(size),
      markupState: initialState.markup
        ? EditorState.createWithContent(convertFromMarkup(initialState.markup))
        : EditorState.createEmpty(),
      mentions: List(mentions || []),
      properties: properties
        ? List(
            properties.map((a: IAssociatedProperty) =>
              AssociatedProperty.fromJS(a),
            ),
          )
        : List<AssociatedProperty>(),
      relatedAttachments: relatedAttachments
        ? List(
            relatedAttachments.map((a: IRelatedAttachment) =>
              RelatedAttachment.fromJS(a),
            ),
          )
        : List<RelatedAttachment>(),
      reminders: reminders
        ? List(reminders.map((a: IReminder) => Reminder.fromJS(a)))
        : List<Reminder>(),
      source: source ? AssignmentSource.fromJS(source) : undefined,
      tags: List(tags || []),
      ...rest,
    })
  }

  public readonly assignedTo: List<AssociatedContact>
  public readonly attachments: List<Attachment>
  public readonly author?: AssociatedContact
  public readonly comments: List<Comment>
  public readonly company?: Company
  public readonly contacts: List<AssociatedContact>
  public readonly deleted: boolean
  public readonly dollarOpportunity: number
  public readonly dueDate?: number
  public readonly hasStarred: List<string>
  public readonly id: string
  public readonly isComplete: boolean
  public readonly label: string
  public readonly lotSize: Area
  public readonly markup: string
  public readonly markupState: EditorState
  public readonly mentions: List<string>
  public readonly priority: AssignmentPriority
  public readonly properties: List<AssociatedProperty>
  public readonly relatedAttachments: List<RelatedAttachment>
  public readonly reminders: List<Reminder>
  public readonly source?: AssignmentSource
  public readonly tags: List<string>
  public readonly title: string

  constructor(initialState?: Partial<Assignment>) {
    initialState ? super(initialState) : super()
  }

  public toJS() {
    return Object.assign(super.toJS(), {
      size: this.lotSize.toJS(),
    })
  }
}
